import type { UseMutationResult, useMutation } from '@tanstack/react-query';
import { upperFirst } from 'lodash-es';

export type UseActionMutationResult<Response, TError, TVariables, ActionName extends string> = {
  [K in 'data' as `${ActionName}Result`]: Response | undefined;
} & {
  [K in 'error' as `${ActionName}${Capitalize<K>}`]: TError | null;
} & {
  [K in 'isLoading' as `is${Capitalize<ActionName>}Loading`]: boolean;
} & {
  [K in 'mutate' as `${ActionName}`]: UseMutationResult<Response, TError, TVariables>['mutate'];
} & {
  [K in 'variables' as `${ActionName}Variables`]: UseMutationResult<
    Response,
    TError,
    TVariables
  >['variables'];
};

export function addActionNameToMutationResult<
  Response,
  TError,
  TVariables,
  ActionName extends string,
>(
  actionName: ActionName,
  mutationResult: ReturnType<typeof useMutation<Response, TError, TVariables>>,
): UseActionMutationResult<Response, TError, TVariables, ActionName> {
  const actionNameCapitalised = upperFirst(actionName);

  return {
    [`${actionName}Result`]: mutationResult.data,
    [`${actionName}Error`]: mutationResult.error,
    [`is${actionNameCapitalised}Loading`]: mutationResult.isPending,
    [actionName]: mutationResult.mutate,
    [`${actionName}Variables`]: mutationResult.variables,
  } as UseActionMutationResult<Response, TError, TVariables, ActionName>;
}
